'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, BookOpen } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import { fetchLatestPostsAction } from '@/app/actions/quote';
import { AircoHomeIcon, AircoServiceIcon } from './AircoIcons';

interface BlogPost {
	id: string;
	title: string;
	description: string;
	slug: string;
	image: string | null;
}

const DIRECTUS_URL = process.env.NEXT_PUBLIC_DIRECTUS_URL || '';

/**
 * Reusable navigation section with links to Home, Onderhoud & Service,
 * and a grid of latest blog posts from Directus.
 */
export default function NavigationLinks() {
	const [posts, setPosts] = useState<BlogPost[]>([]);

	useEffect(() => {
		void fetchLatestPostsAction(7)
			.then((result) => {
				setPosts(result || []);

				return result;
			})
			.catch(() => {});
	}, []);

	return (
		<div className="max-w-7xl mx-auto px-6 py-10 md:py-14">
			<div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10 mb-16">
				{/* Home */}
				<Link href="/" className="group flex items-start gap-5">
					<div className="text-blue-dark group-hover:text-orange transition-colors flex-shrink-0">
						<AircoHomeIcon className="size-14" />
					</div>
					<div>
						<h3 className="text-lg font-heading font-semibold text-blue-dark mb-1 group-hover:text-orange transition-colors">
							Terug naar Home
						</h3>
						<p className="text-base text-gray-500 leading-relaxed mb-2">
							Bekijk ons volledige aanbod en ontdek wat Breuer Klimaattechniek voor u kan betekenen.
						</p>
						<span className="inline-flex items-center gap-1 text-sm font-medium text-orange group-hover:gap-2 transition-all">
							Ga naar Home <ArrowRight className="size-4" />
						</span>
					</div>
				</Link>

				{/* Onderhoud */}
				<Link href="/onderhoud-en-service" className="group flex items-start gap-5">
					<div className="text-blue-dark group-hover:text-orange transition-colors flex-shrink-0">
						<AircoServiceIcon className="size-14" />
					</div>
					<div>
						<h3 className="text-lg font-heading font-semibold text-blue-dark mb-1 group-hover:text-orange transition-colors">
							Onderhoud & Service
						</h3>
						<p className="text-base text-gray-500 leading-relaxed mb-2">
							Uw airco in topconditie houden? Ontdek onze onderhoudsabonnementen en serviceafspraken.
						</p>
						<span className="inline-flex items-center gap-1 text-sm font-medium text-orange group-hover:gap-2 transition-all">
							Meer informatie <ArrowRight className="size-4" />
						</span>
					</div>
				</Link>
			</div>

			{/* Blog Posts Section */}
			{posts?.length > 0 && (
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.3, duration: 0.5 }}
				>
					<div className="flex items-center gap-3 mb-8">
						<BookOpen className="size-7 text-blue-dark" />
						<h3 className="text-xl md:text-2xl font-heading font-semibold text-blue-dark">
							Advies & Inspiratie
						</h3>
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
						{posts.map((post, idx) => (
							<motion.div
								key={post.id}
								initial={{ opacity: 0, y: 15 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.4 + idx * 0.08, duration: 0.4 }}
							>
								<Link href={`/advies/${post.slug}`} className="group block h-full">
									{post.image && (
										<div className="relative w-full h-44 rounded-xl overflow-hidden mb-3 bg-gray-100">
											<Image
												src={`${DIRECTUS_URL}/assets/${post.image}?width=400&height=260&fit=cover`}
												alt={post.title}
												fill
												className="object-cover group-hover:scale-105 transition-transform duration-300"
												sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
											/>
										</div>
									)}
									<h4 className="text-base font-heading font-semibold text-blue-dark mb-2 line-clamp-2 group-hover:text-orange transition-colors">
										{post.title}
									</h4>
									{post.description && (
										<p className="text-sm text-gray-500 line-clamp-2 leading-relaxed mb-2">
											{post.description}
										</p>
									)}
									<span className="inline-flex items-center gap-1 text-sm font-medium text-orange group-hover:gap-2 transition-all">
										Lees meer <ArrowRight className="size-4" />
									</span>
								</Link>
							</motion.div>
						))}

						{/* "Bekijk alle artikelen" tile */}
						<motion.div
							initial={{ opacity: 0, y: 15 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 0.4 + posts.length * 0.08, duration: 0.4 }}
						>
							<Link
								href="/advies"
								className="group flex flex-col items-center justify-center h-full min-h-[220px] rounded-xl bg-blue-light text-center px-6 py-8 hover:bg-blue-dark transition-colors duration-300"
							>
								<BookOpen className="size-12 text-blue-dark group-hover:text-white transition-colors mb-4" />
								<h4 className="text-base font-heading font-semibold text-blue-dark group-hover:text-white transition-colors mb-2">
									Bekijk alle artikelen
								</h4>
								<p className="text-sm text-gray-500 group-hover:text-blue-100 transition-colors mb-3">
									Ontdek meer tips en advies over airconditioning.
								</p>
								<span className="inline-flex items-center gap-1 text-sm font-medium text-orange group-hover:text-white group-hover:gap-2 transition-all">
									Naar overzicht <ArrowRight className="size-4" />
								</span>
							</Link>
						</motion.div>
					</div>
				</motion.div>
			)}
		</div>
	);
}
